import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createVehicle } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import {
  X,
  ChevronRight,
  Check,
  AlertCircle
} from 'lucide-react'

const OnboardingWizard = ({ isOpen, onComplete, onSkip }) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [createdVehicleId, setCreatedVehicleId] = useState(null)

  // Form data
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    make: '',
    model: '',
    trim: '',
    exterior_color: '',
    vin: '',
    mileage: '',
    condition: 'good',
    status: 'with_owner'
  })

  if (!isOpen) return null

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setError(null)
  }

  const handleNext = () => {
    // Validate Step 2 (required fields)
    if (currentStep === 2) {
      if (!formData.year || !formData.make || !formData.model) {
        setError('Year, Make, and Model are required')
        return
      }
    }

    if (currentStep < 4) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      setError(null)
    }
  }

  const handleSubmit = async () => {
    setLoading(true)
    setError(null)

    try {
      // Validate required fields
      if (!formData.year || !formData.make || !formData.model) {
        throw new Error('Year, Make, and Model are required')
      }

      // Prepare vehicle data
      const vehicleData = {
        owner_id: user.id,
        year: parseInt(formData.year),
        make: formData.make.trim(),
        model: formData.model.trim(),
        trim: formData.trim ? formData.trim.trim() : null,
        exterior_color: formData.exterior_color ? formData.exterior_color.trim() : null,
        vin: formData.vin ? formData.vin.trim().toUpperCase() : null,
        mileage: formData.mileage ? parseInt(formData.mileage) : null,
        condition: formData.condition,
        status: formData.status,
        is_active: true
      }

      // Create vehicle
      const { data, error: createError } = await createVehicle(vehicleData)

      if (createError) {
        throw new Error(createError.message || 'Failed to create vehicle')
      }

      if (!data) {
        throw new Error('No vehicle data returned')
      }

      setCreatedVehicleId(data.id)
      setCurrentStep(4) // Go to success step
    } catch (err) {
      console.error('Vehicle creation error:', err)
      setError(err.message || 'Failed to add vehicle. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleComplete = () => {
    if (onComplete) {
      onComplete(createdVehicleId)
    }
  }

  const handleNavigateToGarage = () => {
    navigate(`/garage/${createdVehicleId}`)
    if (onComplete) {
      onComplete(createdVehicleId)
    }
  }

  const handleSkip = () => {
    localStorage.setItem('rdc_onboarding_dismissed', 'true')
    if (onSkip) {
      onSkip()
    }
  }

  const handleAddAnother = () => {
    // Reset form for next vehicle
    setFormData({
      year: new Date().getFullYear(),
      make: '',
      model: '',
      trim: '',
      exterior_color: '',
      vin: '',
      mileage: '',
      condition: 'good',
      status: 'with_owner'
    })
    setCurrentStep(2)
    setError(null)
    setCreatedVehicleId(null)
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-rdc-stone/20 px-6 py-4 flex items-center justify-between">
          {/* Progress dots */}
          <div className="flex gap-2">
            {[1, 2, 3, 4].map(step => (
              <div
                key={step}
                className={`h-2 w-2 rounded-full transition-all ${
                  step === currentStep
                    ? 'bg-rdc-orange w-6'
                    : step < currentStep
                    ? 'bg-green-500'
                    : 'bg-rdc-stone/30'
                }`}
              />
            ))}
          </div>

          {/* Close button */}
          {currentStep !== 4 && (
            <button
              onClick={handleSkip}
              className="p-1 hover:bg-rdc-cream rounded-lg transition-colors"
            >
              <X size={20} className="text-rdc-taupe" />
            </button>
          )}
          {currentStep === 4 && <div />}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Welcome */}
          {currentStep === 1 && (
            <div className="text-center space-y-6">
              <div>
                <h2 className="text-2xl font-display text-black mb-2">
                  Welcome to Digital Garage
                </h2>
                <p className="text-rdc-charcoal">
                  Your automotive community hub. Let's get started!
                </p>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-900">
                    Document your collection
                  </p>
                  <p className="text-xs text-blue-700">
                    Keep detailed records of all your vehicles
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-900">
                    Get AI valuations
                  </p>
                  <p className="text-xs text-green-700">
                    Know the market value of your cars
                  </p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm font-medium text-amber-900">
                    Access concierge services
                  </p>
                  <p className="text-xs text-amber-700">
                    Get expert help with your collection
                  </p>
                </div>
              </div>

              <div className="space-y-3 pt-4">
                <button
                  onClick={handleNext}
                  className="w-full btn-primary py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                >
                  Let's add your first vehicle
                  <ChevronRight size={18} />
                </button>
                <button
                  onClick={handleSkip}
                  className="w-full text-rdc-orange hover:text-rdc-charcoal text-sm font-medium"
                >
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Vehicle Basics */}
          {currentStep === 2 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-xl font-display text-black mb-1">
                  Vehicle Basics
                </h2>
                <p className="text-sm text-rdc-taupe">
                  Tell us about your vehicle
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-3">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <p>{error}</p>
                </div>
              )}

              <div className="space-y-4">
                {/* Year */}
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1900"
                    max={new Date().getFullYear()}
                    value={formData.year}
                    onChange={(e) => handleInputChange('year', e.target.value)}
                    className="w-full px-4 py-2 border border-rdc-stone rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-orange/30"
                  />
                </div>

                {/* Make */}
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Make <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Ford, Porsche, Chevrolet"
                    value={formData.make}
                    onChange={(e) => handleInputChange('make', e.target.value)}
                    className="w-full px-4 py-2 border border-rdc-stone rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-orange/30"
                  />
                </div>

                {/* Model */}
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Model <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Mustang, 911, Corvette"
                    value={formData.model}
                    onChange={(e) => handleInputChange('model', e.target.value)}
                    className="w-full px-4 py-2 border border-rdc-stone rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-orange/30"
                  />
                </div>

                {/* Trim (optional) */}
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Trim <span className="text-rdc-taupe text-xs">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Boss 302, Carrera S"
                    value={formData.trim}
                    onChange={(e) => handleInputChange('trim', e.target.value)}
                    className="w-full px-4 py-2 border border-rdc-stone rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-orange/30"
                  />
                </div>

                {/* Exterior Color (optional) */}
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Exterior Color <span className="text-rdc-taupe text-xs">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Midnight Blue, Pearl White"
                    value={formData.exterior_color}
                    onChange={(e) => handleInputChange('exterior_color', e.target.value)}
                    className="w-full px-4 py-2 border border-rdc-stone rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-orange/30"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Quick Details */}
          {currentStep === 3 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-xl font-display text-black mb-1">
                  Quick Details
                </h2>
                <p className="text-sm text-rdc-taupe">
                  Optional information to complete the profile
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-3">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <p>{error}</p>
                </div>
              )}

              <div className="space-y-4">
                {/* VIN */}
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    VIN <span className="text-rdc-taupe text-xs">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="17 characters"
                    value={formData.vin}
                    onChange={(e) => handleInputChange('vin', e.target.value.toUpperCase())}
                    maxLength="17"
                    className="w-full px-4 py-2 border border-rdc-stone rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-orange/30 font-mono text-sm"
                  />
                  <p className="text-xs text-rdc-taupe mt-1">
                    Found on dashboard or driver's side door jamb
                  </p>
                </div>

                {/* Mileage */}
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Current Mileage <span className="text-rdc-taupe text-xs">(optional)</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    placeholder="e.g., 45000"
                    value={formData.mileage}
                    onChange={(e) => handleInputChange('mileage', e.target.value)}
                    className="w-full px-4 py-2 border border-rdc-stone rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-orange/30"
                  />
                </div>

                {/* Condition */}
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Condition
                  </label>
                  <select
                    value={formData.condition}
                    onChange={(e) => handleInputChange('condition', e.target.value)}
                    className="w-full px-4 py-2 border border-rdc-stone rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-orange/30 bg-white"
                  >
                    <option value="concours">Concours (Show-ready)</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="project">Project</option>
                  </select>
                </div>
              </div>

              <div className="bg-rdc-cream/50 border border-rdc-stone rounded-lg p-4">
                <p className="text-xs text-rdc-taupe">
                  You can always edit these details later in your garage
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {currentStep === 4 && (
            <div className="text-center space-y-6">
              {/* Checkmark animation */}
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <Check size={32} className="text-green-600" strokeWidth={3} />
              </div>

              <div>
                <h2 className="text-2xl font-display text-black mb-2">
                  Success!
                </h2>
                <p className="text-rdc-charcoal">
                  Your {formData.year} {formData.make} {formData.model} has been added to your garage.
                </p>
              </div>

              <div className="bg-rdc-cream/50 border border-rdc-orange/20 rounded-lg p-4">
                <p className="text-sm text-rdc-charcoal">
                  Add photos and service records to start building your provenance score and unlock AI valuations.
                </p>
              </div>

              <div className="space-y-3 pt-4">
                <button
                  onClick={handleNavigateToGarage}
                  className="w-full btn-primary py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                >
                  Go to My Garage
                  <ChevronRight size={18} />
                </button>
                <button
                  onClick={handleAddAnother}
                  className="w-full border-2 border-rdc-orange text-rdc-orange hover:bg-rdc-orange/5 py-3 rounded-lg font-medium transition-colors"
                >
                  Add Another Vehicle
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer with navigation buttons */}
        {currentStep !== 4 && (
          <div className="sticky bottom-0 bg-white border-t border-rdc-stone/20 px-6 py-4 flex gap-3">
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="flex-1 px-4 py-2 border border-rdc-stone text-rdc-charcoal hover:bg-rdc-cream rounded-lg font-medium transition-colors"
              >
                Back
              </button>
            )}

            {currentStep < 3 && (
              <button
                onClick={handleNext}
                disabled={loading}
                className="flex-1 btn-primary py-2 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                Next
                <ChevronRight size={18} />
              </button>
            )}

            {currentStep === 3 && (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 btn-primary py-2 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? 'Adding vehicle...' : 'Add Vehicle'}
                {!loading && <ChevronRight size={18} />}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default OnboardingWizard
